import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@mui/material";

import { partiesListQueryOptions } from "@/lib/queries";
import { PartiesGrid, countLabel } from "@/grids/PartiesGrid";
import { PartyDialog } from "@/components/PartyDialog";
import { useShortcut } from "@/lib/shortcuts";

export const Route = createFileRoute("/_auth/vendors/")({
  component: VendorsListPage,
});

function VendorsListPage() {
  const vendors = useQuery(partiesListQueryOptions({ party_type: "vendor" }));
  const [createOpen, setCreateOpen] = useState(false);
  // Rows the grid shows after its search/filters; null until the grid reports.
  const [shown, setShown] = useState<number | null>(null);

  useShortcut("n", () => setCreateOpen(true), {
    label: "New vendor",
  });

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Vendors</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {countLabel(vendors.data?.length ?? 0, shown)} · click a code to open the vendor
          </span>
          <div className="flex-1" />
          <Link
            to="/customers"
            className="text-sm text-slate-500 hover:text-slate-900 underline dark:text-slate-400"
          >
            view customers →
          </Link>
          <Link
            to="/vendors/merge-tool"
            className="text-sm text-slate-500 hover:text-slate-900 dark:text-slate-400 border border-slate-300 dark:border-gray-700 px-2.5 py-1 rounded hover:bg-slate-50 dark:hover:bg-gray-800"
          >
            Merge duplicates
          </Link>
          <Button
            variant="contained"
            size="small"
            onClick={() => setCreateOpen(true)}
          >
            + New vendor
          </Button>
        </div>
      </div>

      <div className="flex-1 px-6 py-4 overflow-hidden">
        {vendors.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading vendors…</div>
        )}
        {vendors.error && (
          <div className="text-sm text-red-600">
            Error: {(vendors.error as Error).message}
          </div>
        )}
        {vendors.data && <PartiesGrid parties={vendors.data} partyType="vendor" onShownCountChange={setShown} />}
      </div>

      <PartyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        partyType="vendor"
      />
    </div>
  );
}
